import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import ErrorWidget from 'containers/user/home/components/widgets/error-widget';
import NavButton from 'containers/user/home/components/shared/navigation-button';

import { getUserWidgetContent } from 'api/generated/user-widgets/user-widgets';

import { useReports } from 'hooks/api/use-reports';

import powerbi, { pbiModels } from 'utils/powerbi';

export const powerBiWidgetConfig = {
  colSpan: 2,
  rowSpan: {
    default: 9,
  },
};

const Skeleton = (): React.JSX.Element => (
  <div className="animate-pulse w-full h-full flex flex-col space-y-4 p-4 bg-gray-50 rounded-lg">
    <div className="h-6 bg-gray-300 rounded w-1/3"></div>
    <div className="h-4 bg-gray-300 rounded w-2/3"></div>
    <div className="h-48 bg-gray-200 rounded"></div>
    <div className="h-4 bg-gray-300 rounded w-1/4"></div>
  </div>
);

interface PowerBiTokenInfo {
  token: string;
}

interface PowerBiWidgetData {
  powerbiToken: PowerBiTokenInfo;
  powerbiReportId: string;
  powerbiPageId: string;
  powerbiVisualId: string;
  powerbiRelatedReportId?: string;
  powerbiRelatedReportPageId?: string;
}

interface RelatedReport {
  id: string | number;
  powerBiId: string;
  [key: string]: unknown;
}

interface PowerBiWidgetProps {
  userId: string;
  widgetId: string | number;
  customClass?: string;
}

const PowerBiWidget = ({
  userId,
  widgetId,
  customClass,
}: PowerBiWidgetProps): React.JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  const [widgetData, setWidgetData] = useState<PowerBiWidgetData | null>(null);
  const [relatedReport, setRelatedReport] = useState<RelatedReport | undefined>();

  const reports = useReports();

  const visualRef = useRef<HTMLDivElement>(null);

  const loadWidgetData = async (): Promise<void> => {
    setLoading(true);
    try {
      const body = (await getUserWidgetContent(
        userId,
        Number(widgetId),
      )) as { widget?: { content?: PowerBiWidgetData } };
      const data = body.widget?.content;

      const reportsList = reports.data as RelatedReport[] | null | undefined;

      if (data && reportsList?.length && data.powerbiRelatedReportId) {
        setRelatedReport(
          reportsList.find(
            (rpt) => rpt.powerBiId === data.powerbiRelatedReportId,
          ),
        );
      }

      if (data) setWidgetData(data);
    } catch (err) {
      console.error('Error fetching widget data:', err);
      setError(err);
      setLoading(false);
    }
  };

  const loadVisual = (): void => {
    if (!widgetData || !visualRef.current) return;

    const { powerbiToken, powerbiReportId, powerbiPageId, powerbiVisualId } =
      widgetData;

    const embedConfiguration = {
      type: 'visual',
      id: powerbiReportId,
      embedUrl: 'https://app.powerbi.com/visualEmbed',
      tokenType: pbiModels.TokenType.Embed,
      accessToken: powerbiToken.token,
      pageName: powerbiPageId,
      visualName: powerbiVisualId,
      settings: {
        background: pbiModels.BackgroundType.Transparent,
        navContentPaneEnabled: false,
        filterPaneEnabled: false,
      },
    };

    try {
      const visual = powerbi.embed(visualRef.current, embedConfiguration);

      visual.on('rendered', () => setLoading(false));
      visual.on('error', (errorObject: { detail: unknown }) =>
        setError(errorObject.detail),
      );
    } catch (e) {
      console.error('Error embedding Power BI visual:', e);
      setError(e);
    }
  };

  useEffect(() => {
    if (error) return;

    if (widgetData) {
      loadVisual();
    } else {
      loadWidgetData();
    }
  }, [widgetData, error, reports.data]);

  useEffect(() => {
    if (visualRef.current && !error) {
      powerbi.bootstrap(visualRef.current, { type: 'visual' });
    }
  }, [error]);

  if (error) {
    return (
      <div className="p-5 h-full border border-navy-200 rounded-lg">
        <ErrorWidget />
      </div>
    );
  }

  return (
    <div className="relative h-full p-5 border border-navy-200 flex flex-col rounded-lg gap-2.5">
      {loading && (
        <div
          className={classNames(
            'absolute inset-0 flex items-center justify-center bg-white z-10 rounded-lg transition-opacity duration-300',
            { 'opacity-0 pointer-events-none': !loading },
          )}
        >
          <Skeleton />
        </div>
      )}
      <div
        ref={visualRef}
        className={classNames(
          'h-full transition-opacity duration-300',
          customClass,
          { 'opacity-0 pointer-events-none': loading },
        )}
      ></div>
      {relatedReport && (
        <NavButton
          link={`/reports/${relatedReport?.id}`}
          text="View report"
          state={{
            powerbiRelatedReportPageId: widgetData?.powerbiRelatedReportPageId,
          }}
        />
      )}
    </div>
  );
};

export default PowerBiWidget;
